import { useEffect, useRef } from 'react';

import { Box, Button, Flex, Image, Menu, Stack, Text } from '@chakra-ui/react';
import { CaretDown } from '@phosphor-icons/react';

import EmptyImage from '@/assets/image/empty-transcript-cover.svg';
import {
  getScreeningStatusFilterList,
  getScreeningStatusText,
  ScreeningStatus
} from '@/types/enums';
import { Screening } from '@/types/Screening';

import { DateScreening } from './AgentIndex';
import ScreeningCard from './ScreeningCard';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';

interface RecentInterviewsSectionProps {
  recentScreenings: DateScreening[];
  statusFilter: ScreeningStatus | null;
  setStatusFilter: (status: ScreeningStatus | null) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
  onUpdateScreening: (updatedScreening: Screening, removed: boolean) => void;
}

export default function RecentSection({
  recentScreenings,
  statusFilter,
  setStatusFilter,
  isLoading,
  isLoadingMore,
  onLoadMore,
  hasMore,
  onUpdateScreening
}: RecentInterviewsSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight * 1.5 && !isLoadingMore && hasMore) {
        onLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, hasMore, onLoadMore]);

  return (
    <Box
      flex={2}
      ml="1px"
      h="calc(100vh - 200px)"
      w="100%"
      p="32px"
      bg="background.secondary"
      borderRadius="12px"
      borderTopLeftRadius="0"
      boxShadow="sm"
      _hover={{ boxShadow: 'md' }}
      transition="all 0.2s ease-in-out"
    >
      <Flex direction="column" align="flex-start" h="100%">
        <Flex justify="space-between" w="100%" align="center" mb="32px">
          <Text fontSize="24px" fontWeight="bold">
            Recent Sessions
          </Text>
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                variant="outline"
                color="neutrals.700"
                borderRadius="8px"
                p="8px 16px"
                fontSize="14px"
                _hover={{ bg: 'neutrals.100' }}
                disabled={isLoading}
                onClick={(e) => e.stopPropagation()}
              >
                <Flex
                  align="center"
                  w="100%"
                  gap="8px"
                  color={statusFilter ? 'neutrals.900' : 'neutrals.500'}
                >
                  {statusFilter ? getScreeningStatusText(statusFilter) : 'Sort by status'}
                  <CaretDown size="4px" />
                </Flex>
              </Button>
            </Menu.Trigger>
            <Menu.Positioner zIndex={1000}>
              <Menu.Content borderRadius="8px">
                <Menu.Item
                  value=""
                  fontSize="16px"
                  cursor="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusFilter(null);
                  }}
                >
                  <Flex align="center" gap="8px">
                    All
                  </Flex>
                </Menu.Item>
                {getScreeningStatusFilterList().map((status) => (
                  <Menu.Item
                    value={status}
                    fontSize="16px"
                    cursor="pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusFilter(status);
                    }}
                  >
                    <Flex align="center" gap="8px">
                      {getScreeningStatusText(status)}
                    </Flex>
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </Flex>
        <Stack ref={containerRef} gap="32px" w="100%" overflowY="auto" h="calc(100% - 56px)">
          {isLoading ? (
            <LoadingSkeleton count={4} />
          ) : recentScreenings.length > 0 ? (
            <>
              {recentScreenings.map((screenings) => (
                <Stack key={screenings.date.toISOString()}>
                  <Text fontSize="18px" fontWeight="bold" color="neutrals.800" mb="24px">
                    {screenings.date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                  <Stack gap="24px">
                    {screenings.screenings.map((screening) => (
                      <ScreeningCard
                        key={screening.screening_id}
                        screening={screening}
                        onUpdateScreening={onUpdateScreening}
                      />
                    ))}
                  </Stack>
                </Stack>
              ))}
              {isLoadingMore && <LoadingSkeleton count={2} gap="32px" />}
            </>
          ) : (
            <EmptyState />
          )}
        </Stack>
      </Flex>
    </Box>
  );
}

const EmptyState: React.FC = () => (
  <Stack w="100%" h="100%" align="center" role="region" aria-label="No interviews">
    <Image src={EmptyImage} h="280px" w="280px" mt="64px" alt="No interviews illustration" />
    <Text fontSize="24px" color="neutrals.800" fontWeight="bold">
      No Recent Sessions
    </Text>
    <Text fontSize="18px" color="neutrals.500" mb="16px">
      Once a session is set up, it will appear here.
    </Text>
  </Stack>
);
